import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  AddBox as AddStockIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { productSchema, type ProductFormValues } from '@/schemas';
import { FormTextField } from '@/components';
import {
  createProduct,
  getProduct,
  updateProduct,
  type Product,
} from '@/api/products';
import { brand } from '@/theme/theme';

/**
 * Create / Edit Product form.
 *
 * Routes:
 *   /products/new            — create
 *   /products/:id/edit       — edit
 *
 * Validation is driven by `productSchema` (Zod), so the same rules apply
 * to typed input here and to bulk uploads via the API.
 *
 * Save actions:
 *  - Create mode shows two buttons:
 *      "Save product"          → list page (default flow)
 *      "Save & Add Stock"      → AddStockPage for the just-created product
 *  - Edit mode shows a single "Save changes" button — there's nothing
 *    "post-save" the user expects in edit, so the dual-button pattern
 *    would just add noise.
 */
const EMPTY_FORM: ProductFormValues = {
  sku: '',
  name: '',
  description: '',
  brand: '',
  genericName: '',
  packSize: '',
  uom: '',
  hsnCode: '',
  gstRate: undefined,
  mrp: undefined,
  ptr: undefined,
  pts: undefined,
  availableQty: undefined,
  discountPct: undefined,
  scheme: '',
  nearExpiry: '',
};

/** Convert a wire {@code Product} to form values, coercing nulls to empty strings. */
function productToForm(p: Product): ProductFormValues {
  return {
    sku: p.sku ?? '',
    name: p.name ?? '',
    description: p.description ?? '',
    brand: p.brand ?? '',
    genericName: p.genericName ?? '',
    packSize: p.packSize ?? '',
    uom: p.uom ?? '',
    hsnCode: p.hsnCode ?? '',
    gstRate: p.gstRate ?? undefined,
    mrp: p.mrp ?? undefined,
    ptr: p.ptr ?? undefined,
    pts: p.pts ?? undefined,
    availableQty: p.availableQty ?? undefined,
    discountPct: p.discountPct ?? undefined,
    scheme: p.scheme ?? '',
    nearExpiry: p.nearExpiry ?? '',
  };
}

export function ProductFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const numericId = id ? Number(id) : null;

  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(isEdit);

  // Ref tracks WHICH button was clicked without causing a re-render.
  // State tracks whether an API call is in flight (for the loading indicator).
  // Separating the two prevents the button from locking when validation fails
  // (onSubmit never runs on invalid input, so state-based tracking would get stuck).
  const intentRef = useRef<'list' | 'addStock'>('list');
  const [submitMode, setSubmitMode] = useState<'list' | 'addStock' | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: EMPTY_FORM,
  });

  // Separate registration for discountPct so we can wrap onChange with numeric filtering.
  const discountReg = register('discountPct');

  // ---- Edit-mode prefill ----
  useEffect(() => {
    if (!isEdit || numericId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getProduct(numericId);
        if (cancelled) return;
        reset(productToForm(p));
      } catch {
        if (!cancelled) {
          setServerError('Could not load this product. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, numericId, reset]);

  const submitting = submitMode !== null;

  const onSubmit = async (values: ProductFormValues) => {
    // Validation already passed — safe to start the loading state now.
    const intent = intentRef.current;
    setSubmitMode(intent);
    setServerError(null);
    try {
      // Drop empty-string optionals so the backend doesn't store ""
      // for fields the user left blank.
      const cleaned: ProductFormValues = {
        ...values,
        description: values.description?.trim() || undefined,
        brand: values.brand?.trim() || undefined,
        genericName: values.genericName?.trim() || undefined,
        packSize: values.packSize?.trim() || undefined,
        hsnCode: values.hsnCode?.trim() || undefined,
        scheme: values.scheme?.trim() || undefined,
        nearExpiry: values.nearExpiry?.trim() || undefined,
      };

      let saved: Product;
      if (isEdit && numericId != null) {
        saved = await updateProduct(numericId, cleaned);
      } else {
        saved = await createProduct(cleaned);
      }

      if (intent === 'addStock' && saved?.id) {
        navigate(`/products/${saved.id}/stock/new`, {
          replace: true,
          state: { fromCreate: true },
        });
      } else {
        navigate('/products', {
          replace: true,
          state: isEdit ? { updated: true } : { created: true },
        });
      }
    } catch (err) {
      // The axios interceptor normalizes errors to { message, status, errorCode }.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const msg =
        e?.message ||
        (e?.status === 409 && 'A product with this SKU already exists.') ||
        (e?.status === 400 && 'Some fields are invalid. Please review.') ||
        'Failed to save product. Please try again.';
      setServerError(msg);
      setSubmitMode(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
          sx={{ color: brand.inkSoft }}
        >
          Back to products
        </Button>
      </Stack>

      <Typography variant="h2" sx={{ color: brand.navy, mb: 0.5 }}>
        {isEdit ? 'Edit product' : 'New product'}
      </Typography>
      <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 3 }}>
        {isEdit
          ? 'Update this SKU. Required fields are marked with *.'
          : 'Add a new SKU to your catalogue. Required fields are marked with *. Batch and stock are entered separately — use “Save & Add Stock” to jump straight there after creating the product.'}
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>
          {serverError}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              {/* --- Identity --- */}
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="sku"
                  register={register}
                  errors={errors}
                  label="SKU *"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormTextField
                  name="name"
                  register={register}
                  errors={errors}
                  label="Product name *"
                />
              </Grid>

              <Grid item xs={12}>
                <FormTextField
                  name="description"
                  register={register}
                  errors={errors}
                  label="Description"
                  multiline
                  minRows={2}
                />
              </Grid>

              {/* --- Catalog metadata --- */}
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="brand"
                  register={register}
                  errors={errors}
                  label="Brand"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="genericName"
                  register={register}
                  errors={errors}
                  label="Generic name"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="packSize"
                  register={register}
                  errors={errors}
                  label="Pack size"
                  placeholder="e.g. 10x10, 500ml"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="uom"
                  register={register}
                  errors={errors}
                  label="UOM *"
                  placeholder="e.g. PCS, KG, BOX"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="hsnCode"
                  register={register}
                  errors={errors}
                  label="HSN code"
                />
              </Grid>

              {/* --- Pricing --- */}
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="gstRate"
                  register={register}
                  errors={errors}
                  label="GST %"
                  type="number"
                  inputProps={{ step: '0.01', min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="mrp"
                  register={register}
                  errors={errors}
                  label="MRP (₹)"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="availableQty"
                  register={register}
                  errors={errors}
                  label="Available qty"
                  type="number"
                  inputProps={{ step: '1', min: 0 }}
                />
              </Grid>

              {/* --- Pricing tiers (PTR / PTS) --- */}
              <Grid item xs={12} sm={3}>
                <FormTextField
                  name="ptr"
                  register={register}
                  errors={errors}
                  label="PTR (₹)"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormTextField
                  name="pts"
                  register={register}
                  errors={errors}
                  label="PTS — Price To Stockist (₹)"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                />
              </Grid>

              {/* Discount % — numeric text field (no spinner).
                  This is the sale discount offered on PTR / PTS, NOT an MRP-based discount. */}
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  name={discountReg.name}
                  ref={discountReg.ref}
                  onBlur={discountReg.onBlur}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Allow digits and at most one decimal point
                    const stripped = raw.replace(/[^0-9.]/g, '');
                    const [integer, ...rest] = stripped.split('.');
                    const cleaned = rest.length > 0 ? `${integer}.${rest.join('')}` : integer;
                    e.target.value = cleaned;
                    discountReg.onChange(e);
                  }}
                  label="Discount %"
                  type="text"
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.discountPct}
                  helperText={errors.discountPct?.message ?? 'Sale discount on PTR / PTS'}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="scheme"
                  register={register}
                  errors={errors}
                  label="Scheme"
                  placeholder="e.g. 10:1 (buy 10, get 1 free)"
                  helperText="Format: qty:free"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="nearExpiry"
                  register={register}
                  errors={errors}
                  label="Near Expiry"
                  placeholder="mm-yyyy, e.g. 08-2026"
                  helperText="Format: mm-yyyy"
                />
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate('/products')}
                disabled={submitting}
              >
                Cancel
              </Button>

              {/* "Save & Add Stock" is a create-mode-only convenience —
                  nothing equivalent applies when editing an existing
                  product, so the secondary button is suppressed. */}
              {!isEdit && (
                <Button
                  type="submit"
                  variant="outlined"
                  color="primary"
                  disabled={submitting}
                  onClick={() => { intentRef.current = 'addStock'; }}
                  startIcon={
                    submitMode === 'addStock' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AddStockIcon />
                    )
                  }
                >
                  {submitMode === 'addStock' ? 'Saving…' : 'Save & Add Stock'}
                </Button>
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                onClick={() => { intentRef.current = 'list'; }}
                startIcon={
                  submitMode === 'list' ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {submitMode === 'list'
                  ? 'Saving…'
                  : isEdit
                  ? 'Save changes'
                  : 'Save product'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
